import {generateDependencyPin} from "./generate-dependency-pin/generate-dependency-pin"
import {invariant, block} from "@onflow/sdk"
import {log, LEVELS} from "@onflow/util-logger"
import {InteractionTemplate} from "./interaction-template"

export interface VerifyDependencyPinsSameParams {
  template: InteractionTemplate
  blockHeight?: number
  network: string
}

export interface VerifyDependencyPinsSameOpts {
  [key: string]: any
}

export interface VerifyDependencyPinsSameAtLatestSealedBlockParams {
  template: InteractionTemplate
  network: string
}

/**
 * @description Checks if an Interaction Template's pins match those generated at a block height
 *
 * @param params
 * @param params.template Interaction Template to check pins for
 * @param params.blockHeight Block height to check pins at
 * @param params.network Network to check pins on
 * @param opts
 * @returns Whether or not the pins match
 */
export async function verifyDependencyPinsSame(
  {template, blockHeight, network}: VerifyDependencyPinsSameParams,
  opts: VerifyDependencyPinsSameOpts = {}
): Promise<boolean> {
  invariant(
    template != undefined,
    "generateDependencyPin({ template }) -- template must be defined"
  )
  invariant(
    typeof template === "object",
    "generateDependencyPin({ template }) -- template must be an object"
  )
  invariant(
    template.f_type === "InteractionTemplate",
    "generateDependencyPin({ template }) -- template must be an InteractionTemplate"
  )

  invariant(
    network != undefined,
    "generateDependencyPin({ network }) network must be defined"
  )
  invariant(
    blockHeight != undefined,
    "generateDependencyPin({ blockHeight }) blockHeight must be defined"
  )
  invariant(
    typeof blockHeight === "number",
    "generateDependencyPin({ blockHeight }) blockHeight must be a number"
  )

  switch (template.f_version) {
    case "1.0.0":
      const templateDependenciesPlaceholderKeys = Object.keys(
        template.data.dependencies
      )

      for (let templateDependencyPlaceholderKey of templateDependenciesPlaceholderKeys) {
        const templateDependencyPlaceholder =
          template.data.dependencies[templateDependencyPlaceholderKey]

        const templateDependencyPlaceholderContractNames = Object.keys(
          templateDependencyPlaceholder
        )

        for (let templateDependencyPlaceholderContractName of templateDependencyPlaceholderContractNames) {
          const templateDependencyPlaceholderContractNetworks =
            template.data.dependencies[templateDependencyPlaceholderKey][
              templateDependencyPlaceholderContractName
            ]

          const templateDependency =
            templateDependencyPlaceholderContractNetworks[network]
          if (typeof templateDependency === "undefined") continue

          const pin = await generateDependencyPin(
            {
              address: templateDependency.address,
              contractName: templateDependency.contract,
              blockHeight,
            },
            opts
          )

          if (pin !== templateDependency.pin) {
            log({
              title: "verifyDependencyPinsSame Debug Error",
              message: `Could not recompute and match dependency pin.
                                address: ${templateDependency.address} | contract: ${templateDependency.contract}
                                computed: ${pin}
                                template: ${templateDependency.pin}
                            `,
              level: LEVELS.debug,
            })
            return false
          }
        }
      }

      return true

    case "1.1.0":
      let isVerified = false
      // iterate over each dependency
      for (let i = 0; i < template.data?.dependencies.length; i++) {
        const dependency = template.data?.dependencies[i]
        // iterate over each contract in dependency
        for (let j = 0; j < dependency?.contracts.length; j++) {
          const contract = dependency?.contracts[j]
          // iterate over each network in contract
          for (let k = 0; k < contract?.networks.length; k++) {
            const net = contract?.networks[k]
            // if network matches, generate pin and compare
            if (net.network === network) {
              const pin = await generateDependencyPin(
                {
                  version: template.f_version,
                  address: net.address,
                  contractName: contract.contract,
                  blockHeight,
                },
                opts
              )

              if (pin !== (net as any).dependency_pin.pin) {
                log({
                  title: "verifyDependencyPinsSame Debug Error",
                  message: `Could not recompute and match dependency pin.
                                    address: ${net.address} | contract: ${contract.contract}
                                    computed: ${pin}
                                    template: ${(net as any).pin}
                                `,
                  level: LEVELS.debug,
                })
                return false
              } else {
                isVerified = true
              }
            }
          }
        }
      }
      return isVerified

    default:
      throw new Error(
        "verifyDependencyPinsSame Error: Unsupported template version"
      )
  }
}

/**
 * @description Checks if an Interaction Template's pins match those generated at the latest block height
 *
 * @param params
 * @param params.template Interaction Template to check pins for
 * @param params.network Network to check pins on
 * @param opts
 * @returns Whether or not the pins match
 */
export async function verifyDependencyPinsSameAtLatestSealedBlock(
  {template, network}: VerifyDependencyPinsSameAtLatestSealedBlockParams,
  opts: VerifyDependencyPinsSameOpts = {}
): Promise<boolean> {
  const latestSealedBlock = await block({sealed: true})
  const latestSealedBlockHeight = latestSealedBlock?.height

  return verifyDependencyPinsSame(
    {template, network, blockHeight: latestSealedBlockHeight},
    opts
  )
}
